import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Executive sub-roles
const EXECUTIVE_ROLES = {
  PROCESSING: 'processing_executive',
  PACKING: 'packing_executive',
  DISPATCH: 'dispatch_executive'
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const ssoLogin = async (code) => {
    const response = await axios.post(`${API}/auth/sso/exchange`, { code });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name, role) => {    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const isAdmin = () => user?.role === 'admin';
  const isManager = () => user?.role === 'manager';
  const isExecutive = () => user?.role?.includes('executive');
  const isProcessingExecutive = () => user?.role === EXECUTIVE_ROLES.PROCESSING;
  const isPackingExecutive = () => user?.role === EXECUTIVE_ROLES.PACKING;
  const isDispatchExecutive = () => user?.role === EXECUTIVE_ROLES.DISPATCH;
  
  const canManageTasks = () => isAdmin() || isManager() || isDispatchExecutive();
  const canCreateTasks = () => isAdmin() || isManager() || isDispatchExecutive();
  const canProcessing = () => isAdmin() || isManager() || isProcessingExecutive();
  const canPacking = () => isAdmin() || isManager() || isPackingExecutive();

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      login,
      ssoLogin,
      register,
      logout,
      isAdmin,
      isManager,
      isExecutive,
      isProcessingExecutive,
      isPackingExecutive,
      isDispatchExecutive,
      canManageTasks,
      canCreateTasks,
      canProcessing,
      canPacking,
      EXECUTIVE_ROLES
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
